import { createContext, useContext, useState, useMemo, useCallback, useEffect } from 'react';
import { ACTUALS_CUTOFF_2026, SCENARIO_CONFIG, useFinancialData } from './FinancialDataContext';
import { apiFetch } from '../utils/apiFetch';
import { getFirmSetting, setFirmSetting } from '../utils/firmSettings';
import { subscribeFirmSettings } from '../utils/firmSettingsStore';
import { getRecordTypeIdForTier } from '../utils/sfRecordTypes';
import seedData from '../data/pipeline_seed.json';
import {
  PIPELINE_STAGES,
  DIVISION_STAGES,
  SERVICE_TIERS,
  getEffectiveProbability,
  computeAvgDaysToClose,
  calculatePipelineKPIs,
  calculateStageSummary,
  calculateConversionRates,
  calculateStageVelocity,
  calculateSourceStats,
  projectPipelineRevenue,
  getNextStage,
  getNextStageForProspect,
  calculateAnnualizedValue,
} from '../utils/pipelineCalculations';

const PipelineContext = createContext(null);

const LOCALSTORAGE_KEY = 'bcc_pipeline_prospects';

// Fire-and-forget helper to stop a prospect's active email follow-up
// sequence in AC. Called from advanceStage (closed-won), closeAsLost
// (closed-lost), and markUnqualified. Idempotent server-side — safe to
// call when no sequence is active.
function stopFollowupSequence(prospectId, reason) {
  apiFetch('/api/data/email-followup/stop', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ prospectId, reason }),
  }).catch(err => console.warn(`[email-followup] auto-stop (${reason}) failed:`, err));
}
const LS_VERSION_KEY = 'bcc_pipeline_version';
// Bump to force a clear of old test prospects
const PIPELINE_DATA_VERSION = 2;

// Generate a unique prospect ID
function generateId() {
  return 'prospect-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 7);
}

// Get today's date as ISO string (date only)
function todayISO() {
  return new Date().toISOString().split('T')[0];
}

// Read prospects from localStorage as a fallback / migration source only.
// Source of truth is now Supabase via /api/data/prospects.
function readLegacyLocalStorage() {
  try {
    const stored = localStorage.getItem(LOCALSTORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      if (Array.isArray(parsed)) return parsed;
    }
  } catch (e) { /* ignore */ }
  return [];
}

// Async load from API. Returns [].
async function fetchProspectsFromApi() {
  try {
    const res = await apiFetch('/api/data/prospects');
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Failed to fetch prospects');
    return data.prospects || [];
  } catch (e) {
    console.warn('[PipelineContext] fetch failed, falling back to localStorage:', e.message);
    return readLegacyLocalStorage();
  }
}

// Persist (full list) — last-write-wins bulk-upsert.
function persistProspects(prospects) {
  // Keep a localStorage shadow so a failed deploy doesn't lose us a session.
  try { localStorage.setItem(LOCALSTORAGE_KEY, JSON.stringify(prospects)); } catch {}
  // Fire-and-forget bulk-upsert to API.
  apiFetch('/api/data/prospects/bulk-upsert', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(prospects),
  }).then(async (res) => {
    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      console.error('[PipelineContext] prospect bulk-upsert failed:', data);
    }
  }).catch((err) => console.error('[PipelineContext] prospect persist network error:', err));
}

// One-time migration: if API returns empty + localStorage has data, push it up.
async function migrateProspectsIfNeeded() {
  try {
    const live = await fetchProspectsFromApi();
    if (live.length > 0) return live;
    const legacy = readLegacyLocalStorage();
    if (legacy.length === 0) return [];
    console.info('[PipelineContext] Migrating', legacy.length, 'prospects from localStorage to Supabase');
    await apiFetch('/api/data/prospects/bulk-upsert', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(legacy),
    });
    return await fetchProspectsFromApi();
  } catch (e) {
    console.warn('[PipelineContext] migration failed:', e.message);
    return readLegacyLocalStorage();
  }
}

// Compute SF Amount from a prospect's estimatedValue.
// BWM Guided/Total Wealth: annualized recurring (monthly * 12)
// Other: one-time amount
function computeSFAmount(prospect) {
  const v = prospect?.estimatedValue || {};
  const tier = prospect?.serviceTier;
  if (tier === 'guided-wealth' || tier === 'total-wealth') {
    return (v.monthlyRecurring || 0) * 12;
  }
  return v.oneTime || 0;
}

// Push a change to Salesforce. Fire-and-forget — failures are surfaced
// via console.error AND a window.alert so users notice when SF rejects
// a critical write (e.g., RecordType change blocked by validation rule
// or insufficient permissions). Without an alert, the local state shows
// the change but SF never gets it, and the user only finds out at
// close-won when the wrong automation fires.
function pushToSalesforce(prospect, fields) {
  if (!prospect?.sfId) return;
  const fieldList = Object.keys(fields).join(', ');
  apiFetch('/api/sf/update', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ sfId: prospect.sfId, fields }),
  }).then(async (res) => {
    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      const detail = data?.details || data?.error || `HTTP ${res.status}`;
      console.error(`[SF push] failed for "${prospect.name}" (${fieldList}):`, data);
      // Critical-field failures get a visible alert so users know to fix
      // the underlying SF permission/validation issue.
      const critical = ['RecordTypeId', 'StageName', 'stage'].some(k => k in fields);
      if (critical) {
        window.alert(
          `Salesforce rejected an update on "${prospect.name}".\n\n` +
          `Fields attempted: ${fieldList}\n` +
          `Error: ${typeof detail === 'string' ? detail.slice(0, 300) : JSON.stringify(detail).slice(0, 300)}\n\n` +
          `The change is saved locally but did NOT push to SF. Common causes: ` +
          `the integration user lacks Edit Record Type permission, or a SF validation rule rejected the new value.`
        );
      }
    }
  }).catch((err) => console.error('[SF push] network error:', err));
}

// Create a new Opportunity in Salesforce. Returns the SF Id on success.
async function createInSalesforce(prospect) {
  try {
    const payload = {
      name: prospect.company || prospect.name,
      accountName: prospect.company || prospect.name,
      stage: prospect.stage,
      amount: computeSFAmount(prospect),
      closeDate: prospect.expectedCloseDate || null,
      leadSource: prospect.source || null,
      description: prospect.notes || '',
    };
    const res = await apiFetch('/api/sf/create', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    const data = await res.json();
    if (!res.ok || !data.success) {
      console.warn('SF create failed:', data);
      return null;
    }
    return data.sfId;
  } catch (err) {
    console.warn('SF create error:', err);
    return null;
  }
}

export function PipelineProvider({ children }) {
  // Read forecast scenario so the P&L overlay matches Smart Forecast
  const { forecastScenario } = useFinancialData();
  // Hydrate from API (with one-time migration from localStorage if needed).
  // Until the async load completes, expose whatever is in localStorage so
  // the UI isn't blank on cold start.
  const [prospects, setProspects] = useState(readLegacyLocalStorage);

  // Tombstone set: prospect IDs the user explicitly deleted via the
  // "Delete (test data only)" action. Persisted in firm_settings so the
  // SF sync's mergeFromSalesforce path can filter them out — otherwise
  // SF would happily re-create the prospect every sync (the SF
  // Opportunity is still alive even after we delete the BCC row).
  //
  // Stored as an object keyed by id for O(1) lookups: { 'sf-XYZ': true }.
  // To "undelete" a prospect, the user would need to remove the key
  // from firm_settings.deletedProspectIds (advanced — not exposed in UI).
  const [tombstones, setTombstones] = useState(() => {
    const t = getFirmSetting('deletedProspectIds', {});
    return t && typeof t === 'object' ? t : {};
  });

  useEffect(() => {
    let cancelled = false;
    migrateProspectsIfNeeded().then(list => {
      if (!cancelled && Array.isArray(list)) {
        // Filter out tombstoned prospects on initial hydrate too —
        // if a stale row is still in the DB or localStorage shadow,
        // don't show it.
        const t = getFirmSetting('deletedProspectIds', {}) || {};
        setProspects(list.filter(p => !t[p.id]));
      }
    });
    return () => { cancelled = true; };
  }, []);

  // Re-hydrate tombstones when firm_settings finishes its async load
  // (and on any cross-tab firm_settings change).
  useEffect(() => {
    return subscribeFirmSettings((fs) => {
      if (fs && typeof fs.deletedProspectIds === 'object' && fs.deletedProspectIds !== null) {
        setTombstones(fs.deletedProspectIds);
      }
    });
  }, []);

  // Add a new prospect with auto-generated id and initial stageHistory entry.
  // Also creates a matching Opportunity in Salesforce (if connected).
  const addProspect = useCallback((prospect) => {
    const today = todayISO();
    const division = prospect.division || SERVICE_TIERS[prospect.serviceTier]?.division || 'bwm';
    const divPath = DIVISION_STAGES[division] || DIVISION_STAGES.bwm;
    const initialStage = prospect.stage || divPath[0];

    const newProspect = {
      ...prospect,
      id: generateId(),
      division,
      stage: initialStage,
      createdDate: prospect.createdDate || today,
      closedDate: null,
      outcome: null,
      lossReason: null,
      stageHistory: [{ stageId: initialStage, enteredDate: prospect.createdDate || today, exitedDate: null }],
    };

    setProspects((prev) => {
      const updated = [...prev, newProspect];
      persistProspects(updated);
      return updated;
    });

    // Fire-and-forget create in SF; on success, store sfId on the prospect
    createInSalesforce(newProspect).then((sfId) => {
      if (!sfId) return;
      setProspects((prev) => {
        const updated = prev.map(p => p.id === newProspect.id ? { ...p, sfId } : p);
        persistProspects(updated);
        return updated;
      });
    });
  }, []);

  // Update an existing prospect by merging updates.
  // If the prospect is SF-synced, push changes to Salesforce too.
  const updateProspect = useCallback((id, updates) => {
    setProspects((prev) => {
      let changedProspect = null;
      const updated = prev.map(p => {
        if (p.id !== id) return p;
        const merged = { ...p, ...updates };
        changedProspect = merged;
        return merged;
      });
      persistProspects(updated);

      // Push to SF: amount, close date, notes, stage, probability, source, record type
      if (changedProspect?.sfId) {
        const fields = {};
        if (updates.estimatedValue) fields.Amount = computeSFAmount(changedProspect);
        if (updates.expectedCloseDate) fields.CloseDate = updates.expectedCloseDate;
        if ('notes' in updates) fields.Description = updates.notes || '';
        if (updates.stage) fields.stage = updates.stage; // mapped server-side
        // LeadSource — was missing, caused source edits to revert on SF auto-sync
        if ('source' in updates) fields.LeadSource = updates.source || null;
        // Manual probability override -> SF Probability (0-100, percentage)
        if ('manualProbability' in updates) {
          fields.Probability = updates.manualProbability != null
            ? Math.round(updates.manualProbability * 100)
            : null; // null clears it back to stage default
        }
        // serviceTier change -> RecordTypeId. SF automation on close-won
        // is keyed off the record type, so this push is critical for the
        // right post-close behavior to fire (commission booking,
        // onboarding emails, fulfillment routing, etc).
        if ('serviceTier' in updates) {
          const rtId = getRecordTypeIdForTier(updates.serviceTier);
          if (rtId) fields.RecordTypeId = rtId;
        }
        if (Object.keys(fields).length > 0) pushToSalesforce(changedProspect, fields);
      }
      return updated;
    });
  }, []);

  // Delete a prospect by id.
  //
  // CRITICAL: this can NOT use persistProspects() — that does a bulk-upsert
  // of the surviving prospects, which only INSERTs/UPDATEs. It never DELETEs
  // missing rows. So the "deleted" prospect would stay in the DB and come
  // back on the next page load. We must hit the dedicated DELETE endpoint.
  const deleteProspect = useCallback((id) => {
    setProspects((prev) => {
      const updated = prev.filter(p => p.id !== id);
      // Mirror to localStorage shadow so a stale tab doesn't resurrect it
      // via its own next persistProspects() bulk-upsert call.
      try { localStorage.setItem(LOCALSTORAGE_KEY, JSON.stringify(updated)); } catch {}
      return updated;
    });

    // Write tombstone so the SF sync's mergeFromSalesforce path filters
    // this prospect out forever. Without this, an SF-synced prospect
    // (sf-* id) reappears on the next sync because the SF Opportunity is
    // still alive — BCC's local DELETE doesn't propagate to SF.
    const newTombstones = { ...tombstones, [id]: true };
    setTombstones(newTombstones);
    setFirmSetting('deletedProspectIds', newTombstones);

    // Actually delete from the database. The tombstone above prevents
    // re-creation, but we still want the row gone so it doesn't sit as
    // dead weight in Supabase.
    apiFetch(`/api/data/prospects/${encodeURIComponent(id)}`, { method: 'DELETE' })
      .then(async (res) => {
        if (!res.ok) {
          const data = await res.json().catch(() => ({}));
          console.error('[PipelineContext] prospect DELETE failed:', data);
          // Don't alert here — the tombstone will hide it from the UI
          // regardless of whether the row sits in DB.
        }
      })
      .catch((err) => {
        console.error('[PipelineContext] prospect DELETE network error:', err);
      });
  }, [tombstones]);

  // Merge prospects from Salesforce sync.
  // Replaces any existing SF-synced prospects (id starting with 'sf-')
  // and adds new ones, preserving manually-added prospects.
  //
  // Tombstone filter: any prospect ID the user explicitly deleted via
  // "Delete (test data only)" is dropped before merging, even if SF
  // still has the Opportunity alive. Otherwise SF sync would keep
  // re-creating deleted test rows forever. Read from firm_settings at
  // call time (not closure) so freshly added tombstones take effect on
  // the very next sync.
  const mergeFromSalesforce = useCallback(async (sfProspects) => {
    const liveTombstones = getFirmSetting('deletedProspectIds', {}) || {};
    const filteredSf = (sfProspects || []).filter(sf => !liveTombstones[sf.id]);

    // Index incoming SF prospects by their SF Opportunity id (sfId). Used
    // to detect manually-created prospects that have since been pushed to
    // SF and are now coming back as sf-* records — they'd otherwise
    // duplicate (manual id "p-abc" never equals sync id "sf-<oppId>").
    const incomingBySfId = {};
    for (const sf of filteredSf) if (sf.sfId) incomingBySfId[sf.sfId] = sf;

    // Collected outside setProspects so the DB cleanup fires once after
    // the state update. These are manual prospect ids now represented by
    // an incoming SF record — duplicates to hard-delete from Neon.
    const supersededIds = [];

    // Merge against FRESH DB state, not stale in-memory state. Every open
    // BCC tab auto-syncs SF every 5 min; a tab whose local prospect list is
    // hours old would otherwise merge against that stale copy and bulk-upsert
    // it, clobbering an edit just made in another tab ("saves, then reverts
    // ~5 min later" — the reported bug, confirmed with multiple tabs open).
    // Re-fetching first means even a stale tab starts from current DB (which
    // has the latest edits) and preserves them. Fall back to in-memory state
    // if the fetch fails so a transient network error doesn't break the sync.
    // (Residual: a sub-second race remains if an edit lands during the merge;
    // a server-side updated_at guard on bulk-upsert would close that fully.)
    let freshBase = null;
    try { freshBase = await fetchProspectsFromApi(); }
    catch (e) { console.warn('[PipelineContext] merge: fresh fetch failed, using in-memory state:', e.message); }

    const runMerge = (prev) => {
      // A prospect created in BCC gets a local id (not "sf-*") and, once
      // pushed to SF, an sfId. When the SF sync returns that same
      // opportunity, match by sfId so we fold the manual record into the
      // incoming SF record instead of keeping both.
      const supersededManual = {}; // sfId -> the manual prospect (edit source)
      const manual = prev.filter(p => {
        if (p.id?.startsWith('sf-') || liveTombstones[p.id]) return false;
        if (p.sfId && incomingBySfId[p.sfId]) {
          supersededManual[p.sfId] = p;
          if (!supersededIds.includes(p.id)) supersededIds.push(p.id);
          return false; // exclude — the SF record supersedes this manual copy
        }
        return true;
      });
      // Build map of existing SF prospects to preserve manual edits like notes
      const existingSF = {};
      for (const p of prev) {
        if (p.id?.startsWith('sf-') && !liveTombstones[p.id]) existingSF[p.id] = p;
      }
      // For each new SF prospect, merge with existing if any
      const merged = filteredSf.map(sf => {
        // First sync of a manually-created prospect (matched by sfId, no
        // existing sf-* record yet). The user just entered this data, so
        // it's the freshest — keep their explicit fields, but adopt the
        // SF id + sfId so EVERY future sync matches by id cleanly.
        const superseded = sf.sfId ? supersededManual[sf.sfId] : null;
        if (superseded && !existingSF[sf.id]) {
          return {
            ...sf,
            name: superseded.name || sf.name,
            serviceTier: superseded.serviceTier ?? sf.serviceTier,
            division: superseded.division ?? sf.division,
            source: superseded.source ?? sf.source,
            sourceDetail: superseded.sourceDetail ?? sf.sourceDetail,
            notes: superseded.notes || sf.notes,
            stage: superseded.stage || sf.stage,
            stageHistory: superseded.stageHistory?.length > 0 ? superseded.stageHistory : sf.stageHistory,
            manualProbability: superseded.manualProbability,
            estimatedValue: superseded.estimatedValue ?? sf.estimatedValue,
          };
        }
        const existing = existingSF[sf.id];
        if (existing) {
          // If we've locally closed this prospect (won/lost/unqualified),
          // keep our local close state. SF push may not have synced yet, OR
          // we may have closed it for a reason SF doesn't reflect. Local
          // user actions always win for terminal states.
          // Preserve the local estimatedValue when the user has set a real
          // value OR has reclassified the tier away from SF's inference.
          // sfMapper derives estimatedValue from the SF Amount under the
          // tier IT infers; once the user picks a different tier (e.g.
          // BTS "Business Package", which is recurring while SF treated the
          // Amount as a one-time deal), SF's value shape no longer fits and
          // would zero out / mis-bucket the user's monthly fee on every sync.
          const keepLocalValue =
            calculateAnnualizedValue(existing.estimatedValue) > 0 ||
            (existing.serviceTier && existing.serviceTier !== sf.serviceTier);
          const preservedValue = keepLocalValue ? existing.estimatedValue : sf.estimatedValue;

          const TERMINAL = ['closed-won', 'closed-lost', 'unqualified'];
          const localClosed = TERMINAL.includes(existing.stage);
          const sfClosed = TERMINAL.includes(sf.stage);
          // Preserve local terminal stage ONLY when SF agrees the deal is
          // closed. If SF reopened the opportunity (sf.stage is now an
          // active stage like 'proposal' or 'discovery-scheduled'), respect
          // that — staff explicitly changed it in SF and the sync must
          // flow that through. Without this guard, once BCC saw a closed-*
          // stage we'd ignore SF stage changes forever (the Turasky bug
          // where reopened-in-SF prospects stayed closed-lost in BCC).
          if (localClosed && sfClosed) {
            return {
              ...sf,
              stage: existing.stage,
              outcome: existing.outcome,
              closedDate: existing.closedDate,
              lossReason: existing.lossReason,
              manualProbability: existing.manualProbability,
              notes: existing.notes || sf.notes,
              stageHistory: existing.stageHistory || sf.stageHistory,
              estimatedValue: preservedValue,
            };
          }
          // SF-side reopen: clear local close metadata so the prospect
          // re-enters the active pipeline cleanly.
          if (localClosed && !sfClosed) {
            return {
              ...sf,
              // SF stage wins — that's the whole point of the reopen path.
              outcome: null,
              closedDate: null,
              lossReason: null,
              manualProbability: existing.manualProbability,
              notes: existing.notes || sf.notes,
              // Reset stageHistory to SF's so the timeline shows the reopen.
              stageHistory: sf.stageHistory,
              estimatedValue: preservedValue,
            };
          }
          // Otherwise: SF data wins, but preserve fields where local user
          // edits should be authoritative. These all share a common shape:
          // either we don't push them to SF at all, or SF's value is
          // INFERRED from heuristics (RecordType / Name keywords / Type),
          // which is less reliable than an explicit user edit.
          //
          // Specifically preserve:
          //   notes — local edits never round-trip through SF cleanly
          //   stageHistory — local timeline is the source of truth
          //   manualProbability — overrides SF's stage default by design
          //   source / sourceDetail — SF push for source isn't always written
          //   serviceTier / division — INFERRED from SF Record Type +
          //     name keywords by sfMapper. If user explicitly changed
          //     "Guided Wealth" to "Tax Plan" we trust them over heuristics.
          return {
            ...sf,
            notes: existing.notes && existing.notes !== sf.notes ? existing.notes : sf.notes,
            stageHistory: existing.stageHistory?.length > 0 ? existing.stageHistory : sf.stageHistory,
            manualProbability: existing.manualProbability,
            source: existing.source ?? sf.source,
            sourceDetail: existing.sourceDetail ?? sf.sourceDetail,
            serviceTier: existing.serviceTier ?? sf.serviceTier,
            division: existing.division ?? sf.division,
            // estimatedValue: keep the user's value when it's real or when
            // they've reclassified the tier (see keepLocalValue above).
            estimatedValue: preservedValue,
          };
        }
        return sf;
      });
      const updated = [...manual, ...merged];
      return updated;
    };

    // Apply the merge against fresh DB state (preferred) or fall back to
    // in-memory state. Either way persist the result so SF-derived changes
    // land; merging against fresh means we re-persist the latest edits
    // rather than stale ones.
    if (freshBase) {
      const updated = runMerge(freshBase);
      setProspects(updated);
      persistProspects(updated);
    } else {
      setProspects((prev) => {
        const updated = runMerge(prev);
        persistProspects(updated);
        return updated;
      });
    }

    // Hard-delete superseded manual prospects from Neon. persistProspects
    // only upserts — it never deletes — so without this the old manual row
    // (e.g. "Jonathan Katz-Moses" with a local id) would resurrect on the
    // next page load and re-create the duplicate. No tombstone needed: the
    // SF record (sf-<oppId>) is the canonical replacement and has its own
    // id, so nothing should filter it out.
    for (const id of supersededIds) {
      apiFetch(`/api/data/prospects/${encodeURIComponent(id)}`, { method: 'DELETE' })
        .catch(err => console.error('[PipelineContext] superseded prospect DELETE failed:', err));
    }
  }, []);

  // Advance a prospect to the next pipeline stage
  // Closes the current stageHistory entry and opens a new one
  // If advancing to closed-won, sets outcome and closedDate
  // Fires a fire-and-forget marketing time-log auto-create when the
  // new stage is discovery-complete (0.5 hr) or strategy-complete
  // (1.5 hr default, 1.0 hr for tax-plan tier). Idempotent server-side
  // so re-firing for the same prospect+activity is safe.
  const advanceStage = useCallback((id) => {
    setProspects((prev) => {
      let changedProspect = null;
      const updated = prev.map(p => {
        if (p.id !== id) return p;

        const nextStage = getNextStageForProspect(p);
        if (!nextStage) return p;

        const today = todayISO();
        const updatedHistory = p.stageHistory.map(h => {
          if (h.stageId === p.stage && !h.exitedDate) return { ...h, exitedDate: today };
          return h;
        });
        updatedHistory.push({ stageId: nextStage.id, enteredDate: today, exitedDate: null });

        const result = { ...p, stage: nextStage.id, stageHistory: updatedHistory };
        if (nextStage.id === 'closed-won') {
          result.outcome = 'won';
          result.closedDate = today;
        }
        changedProspect = result;
        return result;
      });
      persistProspects(updated);
      // Push back to SF if this is a SF-synced prospect
      if (changedProspect) pushToSalesforce(changedProspect, { stage: changedProspect.stage });
      // Auto-log sales time when entering discovery-complete or
      // strategy-complete. BWM only — we only track CAC for BWM, so
      // BTS/BIA sales-call time stays out of the marketing budget
      // entirely. Fire and forget; failure doesn't block UX.
      if (changedProspect) {
        const division = changedProspect.division || SERVICE_TIERS[changedProspect.serviceTier]?.division;
        if (division === 'bwm') {
          const newStage = changedProspect.stage;
          const activity = newStage === 'discovery-complete' ? 'discovery_call'
                         : newStage === 'strategy-complete'  ? 'strategy_call'
                         : null;
          if (activity) {
            apiFetch('/api/data/marketing/time-log/auto', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ prospectId: changedProspect.id, activityType: activity }),
            }).catch(err => console.warn('[advanceStage] sales-time auto-log failed:', err));
          }
          // Auto-stop email follow-up on Closed Won (sale done — no more
          // follow-up needed; engagement signature isn't tracked separately).
          if (changedProspect.stage === 'closed-won') {
            stopFollowupSequence(changedProspect.id, 'closed-won');
          }
        }
      }
      return updated;
    });
  }, []);

  // Close a prospect as lost with a reason
  const closeAsLost = useCallback((id, lossReason) => {
    setProspects((prev) => {
      const today = todayISO();
      let changedProspect = null;
      const updated = prev.map(p => {
        if (p.id !== id) return p;
        const updatedHistory = p.stageHistory.map(h => {
          if (h.stageId === p.stage && !h.exitedDate) return { ...h, exitedDate: today };
          return h;
        });
        updatedHistory.push({ stageId: 'closed-lost', enteredDate: today, exitedDate: null });
        const result = { ...p, stage: 'closed-lost', outcome: 'lost', closedDate: today, lossReason: lossReason || null, stageHistory: updatedHistory };
        changedProspect = result;
        return result;
      });
      persistProspects(updated);
      if (changedProspect) {
        pushToSalesforce(changedProspect, { stage: 'closed-lost' });
        stopFollowupSequence(changedProspect.id, 'closed-lost');
      }
      return updated;
    });
  }, []);

  // Mark a prospect as unqualified (didn't make it through discovery).
  // Pushes to Salesforce so SF stays in sync; without this push, the next
  // SF auto-sync sees SF still in the prior stage and (despite our
  // local-closed preservation guard) any browser without the unqualified
  // edit would revert it on the next sync cycle.
  const markUnqualified = useCallback((id, reason) => {
    setProspects((prev) => {
      const today = todayISO();
      let changedProspect = null;
      const updated = prev.map(p => {
        if (p.id !== id) return p;

        const updatedHistory = p.stageHistory.map(h => {
          if (h.stageId === p.stage && !h.exitedDate) {
            return { ...h, exitedDate: today };
          }
          return h;
        });

        const result = {
          ...p,
          stage: 'unqualified',
          outcome: 'unqualified',
          closedDate: today,
          lossReason: reason || 'Unqualified',
          stageHistory: [...updatedHistory, { stageId: 'unqualified', enteredDate: today, exitedDate: today }],
        };
        changedProspect = result;
        return result;
      });
      persistProspects(updated);
      // SF push: marker stage + loss reason.
      // STAGE_TO_SF on the server maps 'unqualified' to whatever your SF
      // org uses (today defaults to 'Closed Lost' if no explicit map entry).
      if (changedProspect) {
        pushToSalesforce(changedProspect, {
          stage: 'unqualified',
          // Use SF's standard loss-reason field name. Adjust if your org
          // uses a custom field for unqualified-specific reasoning.
          Loss_Reason__c: reason || 'Unqualified',
        });
        stopFollowupSequence(changedProspect.id, 'unqualified');
      }
      return updated;
    });
  }, []);

  // Reopen a closed prospect back to discovery-scheduled.
  // Also pushes the stage change to SF so the SF Opportunity moves back
  // to the active pipeline.
  const reopenProspect = useCallback((id) => {
    setProspects((prev) => {
      const today = todayISO();
      let changedProspect = null;
      const updated = prev.map(p => {
        if (p.id !== id) return p;

        const updatedHistory = [...p.stageHistory, {
          stageId: 'discovery-scheduled',
          enteredDate: today,
          exitedDate: null,
        }];

        const result = {
          ...p,
          stage: 'discovery-scheduled',
          outcome: null,
          closedDate: null,
          lossReason: null,
          stageHistory: updatedHistory,
        };
        changedProspect = result;
        return result;
      });
      persistProspects(updated);
      if (changedProspect) {
        pushToSalesforce(changedProspect, { stage: 'discovery-scheduled' });
      }
      return updated;
    });
  }, []);

  // Computed values using useMemo for performance
  const pipelineKPIs = useMemo(() => calculatePipelineKPIs(prospects), [prospects]);
  const stageSummary = useMemo(() => calculateStageSummary(prospects), [prospects]);
  const conversionRates = useMemo(() => calculateConversionRates(prospects), [prospects]);
  const stageVelocity = useMemo(() => calculateStageVelocity(prospects), [prospects]);
  const monthlyForecast = useMemo(() => projectPipelineRevenue(prospects), [prospects]);
  const sourceStats = useMemo(() => calculateSourceStats(prospects), [prospects]);

  // Baseline forecast revenue per division per month (for P&L overlay)
  // Uses the same Smart Forecast logic: historical velocity * seasonal * conversion * deal value
  const baselineForecastRevenue = useMemo(() => {
    const DIVISIONS = ['bwm', 'bts', 'bia'];
    const DEFAULT_MONTHLY_PROSPECTS = { bwm: 2, bts: 3, bia: 1 };
    const DEFAULT_CONVERSION = { bwm: 0.25, bts: 0.40, bia: 0.60 };
    // Days-to-close computed from won deals; falls back to defaults if no data
    const DEFAULT_DAYS_TO_CLOSE = computeAvgDaysToClose(prospects || []);
    const DEFAULT_DEAL_VALUES = { bwm: 14850, bts: 2250, bia: 500 };
    const SEASONAL = {
      bts: [1.5, 1.5, 1.5, 1.5, 0.6, 0.6, 0.6, 0.6, 1.3, 1.3, 0.7, 0.7],
      bwm: [1.1, 1.1, 1.1, 1.0, 1.0, 1.0, 0.9, 0.9, 0.9, 1.0, 1.0, 1.0],
      bia: [1.0, 1.0, 1.0, 1.0, 1.0, 1.0, 1.0, 1.0, 1.0, 1.0, 1.0, 1.0],
    };
    const ACTUALS_CUTOFF = ACTUALS_CUTOFF_2026;

    // Compute velocity per division
    const monthsWithData = new Set();
    const monthCounts = {};
    for (const div of DIVISIONS) monthCounts[div] = {};
    for (const p of (prospects || [])) {
      if (!p.createdDate) continue;
      const div = p.division || SERVICE_TIERS[p.serviceTier]?.division;
      if (!div || !DIVISIONS.includes(div)) continue;
      const mi = new Date(p.createdDate).getMonth();
      monthCounts[div][mi] = (monthCounts[div][mi] || 0) + 1;
      monthsWithData.add(mi);
    }
    const numMonths = monthsWithData.size || 1;
    const rates = {};
    for (const div of DIVISIONS) {
      const total = Object.values(monthCounts[div]).reduce((s, v) => s + v, 0);
      rates[div] = total > 0 ? total / numMonths : DEFAULT_MONTHLY_PROSPECTS[div];
    }

    // Compute deal values
    const dealValues = {};
    for (const div of DIVISIONS) {
      const divProspects = (prospects || []).filter(p => (p.division || SERVICE_TIERS[p.serviceTier]?.division) === div);
      const withVal = divProspects.filter(p => calculateAnnualizedValue(p.estimatedValue) > 0);
      dealValues[div] = withVal.length >= 2 ? Math.round(withVal.reduce((s, p) => s + calculateAnnualizedValue(p.estimatedValue), 0) / withVal.length) : DEFAULT_DEAL_VALUES[div];
    }

    // Compute conversion rates
    const conv = {};
    for (const div of DIVISIONS) {
      const divP = (prospects || []).filter(p => (p.division || SERVICE_TIERS[p.serviceTier]?.division) === div);
      let won = 0, total = 0;
      for (const p of divP) {
        if (p.outcome === 'won') { won++; total++; }
        else if (p.outcome === 'lost' || p.outcome === 'unqualified') { total++; }
      }
      conv[div] = total >= 3 ? won / total : DEFAULT_CONVERSION[div];
    }

    // Apply scenario multipliers (matches Smart Forecast)
    const scenarioCfg = SCENARIO_CONFIG[forecastScenario] || SCENARIO_CONFIG.normal;
    const velMult = scenarioCfg.velocityMultiplier;
    const pipMult = scenarioCfg.pipelineMultiplier;
    const minStageOrder = scenarioCfg.pipelineMinStageOrder || 1;
    const convAdj = scenarioCfg.conversionAdjust;
    const adjustedConv = {};
    for (const div of DIVISIONS) {
      adjustedConv[div] = Math.max(0.05, Math.min(0.95, conv[div] + convAdj));
    }

    // Helper to recognize revenue for a single deal value at close month
    function recognizeForDiv(div, value, closeMonth, grid) {
      if (div === 'bts') {
        const recogMonth = Math.max(closeMonth, 3);
        if (recogMonth < 12) grid[div][recogMonth] += Math.round(value);
      } else if (div === 'bwm') {
        const oneTime = value * 0.4;
        const recurring = (value * 0.6) / 12;
        if (closeMonth < 12) grid[div][closeMonth] += Math.round(oneTime * 0.5);
        if (closeMonth + 3 < 12) grid[div][closeMonth + 3] += Math.round(oneTime * 0.5);
        for (let rm = closeMonth; rm < 12; rm++) grid[div][rm] += Math.round(recurring);
      } else {
        if (closeMonth < 12) grid[div][closeMonth] += Math.round(value);
      }
    }

    // ---- LAYER 2: Pipeline (existing active prospects with effective probability) ----
    const pipelineGrid = { global: new Array(12).fill(0), bwm: new Array(12).fill(0), bts: new Array(12).fill(0), bia: new Array(12).fill(0) };
    const pipelineDealsGrid = { bwm: new Array(12).fill(0), bts: new Array(12).fill(0), bia: new Array(12).fill(0) };
    const activeProspects = (prospects || []).filter(p =>
      p.stage !== 'closed-won' && p.stage !== 'closed-lost' && p.stage !== 'unqualified'
    );
    for (const p of activeProspects) {
      const div = p.division || SERVICE_TIERS[p.serviceTier]?.division;
      if (!DIVISIONS.includes(div)) continue;
      // Filter by min stage order for conservative scenario
      const stage = PIPELINE_STAGES.find(s => s.id === p.stage);
      if (!stage || stage.order < minStageOrder) continue;

      const annualValue = calculateAnnualizedValue(p.estimatedValue);
      const { probability } = getEffectiveProbability(p);
      const weightedValue = annualValue * probability * pipMult;
      if (weightedValue <= 0) continue;

      // Determine close month
      let closeMonth;
      if (p.expectedCloseDate) {
        const d = new Date(p.expectedCloseDate);
        if (d.getFullYear() === 2026) closeMonth = d.getMonth();
      }
      if (closeMonth == null) {
        const today = new Date();
        const daysOut = DEFAULT_DAYS_TO_CLOSE[div] || 30;
        const projected = new Date(today.getTime() + daysOut * 24 * 60 * 60 * 1000);
        closeMonth = projected.getFullYear() === 2026 ? projected.getMonth() : 11;
      }
      closeMonth = Math.max(ACTUALS_CUTOFF, Math.min(closeMonth, 11));

      recognizeForDiv(div, weightedValue, closeMonth, pipelineGrid);
      pipelineDealsGrid[div][closeMonth] += probability * pipMult;
    }

    // ---- LAYER 3: Velocity (new prospects, pipeline-aware) ----
    const velocityGrid = { bwm: new Array(12).fill(0), bts: new Array(12).fill(0), bia: new Array(12).fill(0) };
    const velocityDealsGrid = { bwm: new Array(12).fill(0), bts: new Array(12).fill(0), bia: new Array(12).fill(0) };

    for (let m = ACTUALS_CUTOFF; m < 12; m++) {
      for (const div of DIVISIONS) {
        const prospectCount = rates[div] * (SEASONAL[div]?.[m] || 1.0) * velMult;
        const expectedDeals = prospectCount * adjustedConv[div];
        const daysToClose = DEFAULT_DAYS_TO_CLOSE[div];
        const closeMonth = Math.min(m + Math.round(daysToClose / 30), 11);

        recognizeForDiv(div, dealValues[div] * expectedDeals, closeMonth, velocityGrid);
        velocityDealsGrid[div][closeMonth] += expectedDeals;
      }
    }

    // Pipeline-aware velocity: subtract overlap
    const adjustedVelocity = { bwm: new Array(12).fill(0), bts: new Array(12).fill(0), bia: new Array(12).fill(0) };
    for (let m = 0; m < 12; m++) {
      for (const div of DIVISIONS) {
        const velDeals = velocityDealsGrid[div][m];
        const pipeDeals = pipelineDealsGrid[div][m];
        if (velDeals <= 0) continue;
        const overlap = Math.min(pipeDeals, velDeals);
        const surplusFrac = velDeals > 0 ? Math.max(0, (velDeals - overlap) / velDeals) : 0;
        adjustedVelocity[div][m] = velocityGrid[div][m] * surplusFrac;
      }
    }

    // ---- COMBINE: pipeline + adjusted velocity ----
    const result = { global: new Array(12).fill(0), bwm: new Array(12).fill(0), bts: new Array(12).fill(0), bia: new Array(12).fill(0) };
    for (let m = 0; m < 12; m++) {
      for (const div of DIVISIONS) {
        result[div][m] = pipelineGrid[div][m] + adjustedVelocity[div][m];
      }
      result.global[m] = result.bwm[m] + result.bts[m] + result.bia[m];
    }

    return result;
  }, [prospects, forecastScenario]);

  const value = useMemo(() => ({
    prospects,
    addProspect,
    updateProspect,
    deleteProspect,
    mergeFromSalesforce,
    advanceStage,
    closeAsLost,
    markUnqualified,
    reopenProspect,
    pipelineKPIs,
    stageSummary,
    conversionRates,
    stageVelocity,
    monthlyForecast,
    sourceStats,
    baselineForecastRevenue,
  }), [
    prospects,
    addProspect,
    updateProspect,
    deleteProspect,
    mergeFromSalesforce,
    advanceStage,
    closeAsLost,
    markUnqualified,
    reopenProspect,
    pipelineKPIs,
    stageSummary,
    conversionRates,
    stageVelocity,
    monthlyForecast,
    sourceStats,
    baselineForecastRevenue,
  ]);

  return (
    <PipelineContext.Provider value={value}>
      {children}
    </PipelineContext.Provider>
  );
}

export function usePipeline() {
  const ctx = useContext(PipelineContext);
  if (!ctx) throw new Error('usePipeline must be used within PipelineProvider');
  return ctx;
}
